import { apiFetch } from '../api/client';
import { dataViaApi } from '../api/mode';
import type { ReminderEventType, ReminderRules } from './reminderSchedule';

/**
 * B5 (owner rulings 2026-10-25): the reminder & escalation history of a
 * store or task, with recipients by name, and the Notification rules'
 * timing (api/src/reminders.ts). Server mode only — in direct (rollback)
 * mode NotificationLog is Admin-only, so the panels stay hidden (null).
 */
export interface ReminderEntry {
  at: string;
  type: ReminderEventType;
  sent: boolean;
  to: string[];
  /** The store the email was about. */
  storeId: number | null;
  taskId: number | null;
  templateId: number | null;
  /** Admins only: the flow's error text. */
  detail?: string;
}

export interface ReminderView {
  entries: ReminderEntry[];
  rules: ReminderRules;
  /** DM clocks' escalation contact (district, else global) — '' when unknown. */
  escalationName: string;
  /** Auditor clocks escalate to the GLOBAL target only (Flow 1). */
  globalEscalationName: string;
  dmName: string;
}

export async function getStoreReminders(storeId: string | number, days: 90 | 365 = 90): Promise<ReminderView | null> {
  if (!dataViaApi()) return null;
  return apiFetch<ReminderView>(`/reminders?storeId=${encodeURIComponent(String(storeId))}&days=${days}`);
}

export async function getTaskReminders(taskId: number): Promise<ReminderView | null> {
  if (!dataViaApi()) return null;
  return apiFetch<ReminderView>(`/reminders?taskId=${encodeURIComponent(String(taskId))}`);
}

/** Reports → Reminders & escalations sent: every visible store's entries of the last `days` (docs/reports.md #3). */
export interface ReminderFeed {
  entries: ReminderEntry[];
  /** More rows than the server's cap were sent in the window — the oldest are missing. */
  truncated: boolean;
  days: number;
}

export const FEED_DAYS = [7, 30, 90] as const;
export type FeedDays = (typeof FEED_DAYS)[number];

export async function getReminderFeed(days: FeedDays): Promise<ReminderFeed | null> {
  if (!dataViaApi()) return null;
  return apiFetch<ReminderFeed>(`/reminders?scope=visible&days=${days}`);
}
